import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import SnapshotImg from '../SnapshotImg';
import {
  DEFAULT_ASPECT,
  centralMinute,
  getCfHeaders,
  snapshotUrl,
  streamUrl,
  todayCentral,
} from '../../utils/cameraRelay';
import CameraTile, { ActivityLine, LiveBadge, cameraProblem, healthFor } from './CameraTile';
import DayBar, { RANGE_WORKDAY, runsToBlocks } from './DayBar';
import { C, Icon, KIND_DOT } from './CamerasStyles';
import { useStationRuns } from './useCameraData';
import { tileAlert, tileWatch } from '../../features/security/nightWatch';
import { doorChipsForCamera } from '../../features/security/doors';

export const TILE_SIZES = [
  { key: 'l', label: 'Large', min: 420 },
  { key: 'm', label: 'Medium', min: 320 },
  { key: 's', label: 'Small', min: 240 },
];

const STATE_LABEL = {
  working: 'Working',
  paused: 'Paused',
  maintenance: 'Maintenance',
  waiting: 'Waiting',
  idle: 'Idle',
};

function FocusView({ camera, cameras, aspects, activity, health, onBack, onPick, onOpenPlayback }) {
  const frameRef = useRef(null);
  const index = cameras.findIndex((c) => c.key === camera.key);
  const prev = cameras[(index - 1 + cameras.length) % cameras.length];
  const next = cameras[(index + 1) % cameras.length];
  const act = camera.jobqStation ? activity[camera.jobqStation] : null;
  const problem = cameraProblem(healthFor(health, camera));
  const today = todayCentral();
  const runsState = useStationRuns(camera.jobqStation ? [camera.jobqStation] : [], today);
  const runs = camera.jobqStation ? runsState[camera.jobqStation]?.runs || [] : [];
  const aspect = aspects[camera.key] || camera.aspect || DEFAULT_ASPECT;

  // Esc goes back to the grid; ← / → step through cameras.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (/INPUT|TEXTAREA|SELECT/.test(tag) || e.target?.getAttribute?.('role') === 'slider') return;
      if (e.key === 'Escape') onBack();
      else if (e.key === 'ArrowLeft') onPick(prev);
      else if (e.key === 'ArrowRight') onPick(next);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onBack, onPick, prev, next]);

  const fullScreen = () => {
    const el = frameRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  return (
    <main style={{ display: 'flex', flexWrap: 'wrap', gap: 18, padding: '14px 24px 28px', alignItems: 'flex-start' }}>
      <section style={{ flex: '999 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <button type="button" className="jqc-btn" onClick={onBack}><Icon name="back" />All cameras</button>
          <h2 style={{ margin: '0 6px 0 4px', fontSize: 18, fontWeight: 600 }}>
            {camera.station} {camera.sub && <span style={{ color: C.muted, fontWeight: 400 }}>{camera.sub}</span>}
          </h2>
          <LiveBadge problem={problem} />
          {problem && <span style={{ color: C.warn, fontSize: 12 }}>{problem}</span>}
          <span style={{ flex: 1 }} />
          <button type="button" className="jqc-btn" onClick={() => onOpenPlayback(camera, today, Math.max(0, centralMinute() - 5))}>
            <Icon name="rewind" />Rewind 5 min
          </button>
          <button type="button" className="jqc-btn" onClick={() => onOpenPlayback(camera, today, null)}>Open in Playback</button>
          <button type="button" className="jqc-btn jqc-icon" aria-label="Full screen" onClick={fullScreen}><Icon name="expand" /></button>
        </div>

        <div ref={frameRef} className="jqc-frame" style={{ aspectRatio: String(aspect), borderRadius: 12, border: `1px solid ${C.border}` }}>
          {/* key forces a fresh MJPEG connection per camera */}
          <img key={camera.key} className="jqc-contain" src={streamUrl(camera.station, camera.index)} alt={`${camera.label}, live`} />
          {cameras.length > 1 && (
            <>
              <button type="button" className="jqc-nav" style={{ left: 12 }} aria-label={`Previous camera: ${prev.label}`} onClick={() => onPick(prev)}><Icon name="left" size={22} /></button>
              <button type="button" className="jqc-nav" style={{ right: 12 }} aria-label={`Next camera: ${next.label}`} onClick={() => onPick(next)}><Icon name="right" size={22} /></button>
            </>
          )}
        </div>

        {camera.jobqStation ? (
          <div className="jqc-card" style={{ padding: '14px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', minWidth: 0 }}>
              {act && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 24, padding: '0 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: act.kind === 'working' ? '#1c3557' : '#262b31', color: act.kind === 'working' ? '#cfe3ff' : C.textSoft }}>
                  <span className="jqc-dot" style={{ background: KIND_DOT[act.kind] || '#5f6771' }} />
                  {STATE_LABEL[act.kind] || 'Unknown'}
                </span>
              )}
              <span className="jqc-ell" style={{ fontWeight: 500, flex: '1 1 260px' }}>{act ? act.text.replace(/^Paused · /, '') : 'Loading JobQ…'}</span>
              {act?.meta && <span style={{ color: C.muted, fontSize: 12 }}>{act.meta}</span>}
              {act?.jobId && <Link to={`/jobs/${act.jobId}`} style={{ fontSize: 13, color: C.focus }}>Open job</Link>}
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{camera.jobqStation} today · click to watch that moment</div>
              <DayBar
                lanes={[{ key: 'jobq', label: 'JobQ work', height: 26, blocks: runsToBlocks(runs) }]}
                range={RANGE_WORKDAY}
                playhead={null}
                now={centralMinute()}
                onSeek={(m) => onOpenPlayback(camera, today, m)}
                ariaLabel={`${camera.jobqStation} today`}
              />
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, color: C.faint, fontSize: 13 }}>This camera isn't tied to a JobQ station, so there's no job activity under it.</p>
        )}
      </section>

      <aside style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h3 style={{ margin: '0 0 2px', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.faint }}>Other cameras</h3>
        {cameras.filter((c) => c.key !== camera.key).map((c) => (
          <button key={c.key} type="button" className="jqc-mini" onClick={() => onPick(c)} aria-label={`Watch ${c.label}`}>
            <span className="jqc-frame jqc-r-std" style={{ width: 116, flex: 'none', borderRadius: 6 }}>
              <SnapshotImg
                src={snapshotUrl(c.station, c.index, 60)}
                alt=""
                authHeaders={getCfHeaders()}
                intervalMs={4000}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{c.station} {c.sub && <span style={{ color: C.muted, fontWeight: 400 }}>{c.sub}</span>}</span>
              <ActivityLine camera={c} activity={c.jobqStation ? activity[c.jobqStation] : null} compact />
            </span>
          </button>
        ))}
      </aside>
    </main>
  );
}

export default function LiveView({
  cameras, aspects, onFrameSize, activity, health,
  filter, onFilter, focusKey, onFocus, size, onSize, onOpenPlayback, security = null,
}) {
  const stations = useMemo(() => {
    const out = [];
    for (const c of cameras) {
      const s = out.find((x) => x.name === c.station);
      if (s) s.count += 1;
      else out.push({ name: c.station, count: 1 });
    }
    return out;
  }, [cameras]);

  const shown = filter === 'All' ? cameras : cameras.filter((c) => c.station === filter);
  const focusCam = focusKey ? cameras.find((c) => c.key === focusKey) : null;
  const min = (TILE_SIZES.find((s) => s.key === size) || TILE_SIZES[0]).min;

  if (focusCam) {
    return (
      <FocusView
        camera={focusCam}
        cameras={cameras}
        aspects={aspects}
        activity={activity}
        health={health}
        onBack={() => onFocus(null)}
        onPick={(c) => onFocus(c.key)}
        onOpenPlayback={onOpenPlayback}
      />
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '14px 24px 0' }}>
        {['All', ...stations.map((s) => s.name)].map((name) => {
          const count = stations.find((s) => s.name === name)?.count || 0;
          return (
            <button key={name} type="button" className="jqc-chip" aria-pressed={filter === name} onClick={() => onFilter(name)}>
              {name}
              {count > 1 && <span style={{ fontSize: 11, opacity: 0.65 }}>{count}</span>}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        <div className="jqc-seg" role="group" aria-label="Tile size">
          {TILE_SIZES.map((s) => (
            <button key={s.key} type="button" aria-pressed={size === s.key} onClick={() => onSize(s.key)}>{s.label}</button>
          ))}
        </div>
      </div>
      {cameras.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: C.faint }}>No cameras reported by the relay.</div>
      ) : (
        <main className="jqc-grid" style={{ padding: '14px 24px 28px', '--jqc-min': `${min}px` }}>
          {shown.map((c) => (
            <CameraTile
              key={c.key}
              camera={c}
              aspect={aspects[c.key] || c.aspect}
              activity={c.jobqStation ? activity[c.jobqStation] : null}
              health={healthFor(health, c)}
              watch={tileWatch(security, c.key)}
              lastAlert={tileAlert(security, c.key)}
              doorChips={doorChipsForCamera(security?.doors, c.key)}
              onOpen={(cam) => onFocus(cam.key)}
              onFrameSize={onFrameSize}
            />
          ))}
        </main>
      )}
    </>
  );
}
